import db from '@/db';
import { isTrainingMode } from '@/lib/training';
import { decrementBeverageStock, incrementBeverageStock } from '@/lib/stock';
import { logAudit } from '@/lib/audit';
import { addTombstone, getTombstones } from '@/lib/tombstones';
import { recordSale, assertSessionOpenIfRequired } from '@/lib/cashDrawer';

export const COMMAND_STATUS = {
  open: { label: 'Aberta', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  closed: { label: 'Fechada', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  paid: { label: 'Paga', color: 'bg-blue-100 text-blue-700 border-blue-200' },
};

// Cria ou reabre comanda por número. Se já existir aberta, retorna ela.
// Fase 25: ignora comandas arquivadas (encerramento do dia zerou a numeração).
// Fase 31.2: bloqueio opcional de criação sem sessão de caixa aberta.
export async function getOrCreateCommand(number, operatorId) {
  const num = Number(number);
  if (!num || num <= 0) throw new Error('Número de comanda inválido');
  const all = await db.commands.where('number').equals(num).toArray();
  const existing = all.find((c) => !c.archivedAt);
  if (existing) {
    if (existing.status !== 'open') {
      throw new Error(`Comanda #${num} já foi fechada`);
    }
    return { command: existing, created: false };
  }
  // Bloqueio (apenas para criar novas — comandas já abertas continuam editáveis)
  await assertSessionOpenIfRequired();
  const training = await isTrainingMode();
  const id = await db.commands.add({
    number: num,
    status: 'open',
    weightEntries: [],
    items: [],
    buffetTotal: 0,
    itemsTotal: 0,
    total: 0,
    operatorId,
    isTraining: training,
    createdAt: new Date().toISOString(),
    closedAt: null,
    paidAt: null,
    paymentMethod: null,
    archivedAt: null,
  });
  const command = await db.commands.get(id);
  return { command, created: true };
}

/**
 * Fase 32 — Lógica unificada do leitor:
 *  - Se existe comanda aberta não-arquivada para esse número, carrega ela.
 *  - Se existe comanda PAGA não-arquivada (cartão sendo re-entregue pro próximo
 *    cliente), arquiva a paga e cria uma nova aberta com o mesmo número.
 *  - Se não existe nenhuma, cria nova.
 * Retorna `{ command, mode: 'loaded' | 'reused' | 'created' }`.
 */
export async function getOrReuseCommand(number, operatorId) {
  const num = Number(number);
  if (!num || num <= 0) throw new Error('Número de comanda inválido');

  // Hotfix: blindagem para UnknownError do Dexie/IDB. Se a transaction falhar
  // (DB recém-aberto após migration ou storage corrompido), reabrimos e
  // tentamos uma vez mais com fallback explícito.
  let all;
  try {
    all = await db.commands.where('number').equals(num).toArray();
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[getOrReuseCommand] toArray falhou — tentando reabrir DB:', err?.message || err);
    try {
      if (!db.isOpen()) await db.open();
      all = await db.commands.where('number').equals(num).toArray();
    } catch (err2) {
      // eslint-disable-next-line no-console
      console.error('[getOrReuseCommand] retry falhou:', err2);
      throw new Error(`Banco local indisponível: ${err2?.message || err2}`);
    }
  }

  const active = all.find((c) => !c.archivedAt);

  if (active && active.status === 'open') {
    return { command: active, mode: 'loaded' };
  }

  if (active && active.status !== 'open') {
    // Comanda paga/fechada → arquiva para reutilizar o número
    await db.commands.update(active.id, {
      archivedAt: new Date().toISOString(),
      archivedReason: 'card_reuse',
    });
  }

  // Bloqueio (apenas para criar novas)
  await assertSessionOpenIfRequired();
  const training = await isTrainingMode();
  const id = await db.commands.add({
    number: num,
    status: 'open',
    weightEntries: [],
    items: [],
    buffetTotal: 0,
    itemsTotal: 0,
    total: 0,
    operatorId,
    isTraining: training,
    createdAt: new Date().toISOString(),
    closedAt: null,
    paidAt: null,
    paymentMethod: null,
    archivedAt: null,
  });
  const command = await db.commands.get(id);
  return { command, mode: active ? 'reused' : 'created' };
}

// Adiciona peso ao histórico e atualiza buffetTotal
export async function appendWeightToCommand(number, weightKg, pricePerKg, operatorId) {
  const { command } = await getOrCreateCommand(number, operatorId);
  const entry = {
    weight: weightKg,
    pricePerKg,
    subtotal: weightKg * pricePerKg,
    at: new Date().toISOString(),
  };
  const weightEntries = [...(command.weightEntries || []), entry];
  const buffetTotal = weightEntries.reduce((a, e) => a + e.subtotal, 0);
  const total = buffetTotal + (command.itemsTotal || 0);
  await db.commands.update(command.id, { weightEntries, buffetTotal, total });
  return db.commands.get(command.id);
}

// Adiciona um item (bebida) na comanda aberta
export async function addItemToCommand(number, item, operatorId) {
  const { command } = await getOrCreateCommand(number, operatorId);
  const newItem = {
    name: item.name,
    price: Number(item.price),
    quantity: Number(item.quantity || 1),
    subtotal: Number(item.price) * Number(item.quantity || 1),
    at: new Date().toISOString(),
    beverageId: item.beverageId || null,
  };
  const items = [...(command.items || []), newItem];
  const itemsTotal = items.reduce((a, i) => a + i.subtotal, 0);
  const total = (command.buffetTotal || 0) + itemsTotal;
  await db.commands.update(command.id, { items, itemsTotal, total });
  // Fase 20 (P0) — baixa automática de estoque de bebidas em comandas modernas.
  // Silencioso: se a bebida não existir em /estoque, ignora (ex: item livre digitado).
  decrementBeverageStock({
    beverageId: newItem.beverageId,
    name: newItem.name,
    qty: newItem.quantity,
  }).catch(() => {});
  return db.commands.get(command.id);
}

export async function closeCommand(commandId, paymentMethod, paymentDetails = {}, operator = null) {
  const now = new Date().toISOString();
  const patch = {
    status: 'paid',
    paymentMethod,
    closedAt: now,
    paidAt: now,
  };
  if (paymentDetails.amountReceived !== undefined) {
    patch.amountReceived = Math.round(Number(paymentDetails.amountReceived) * 100) / 100;
  }
  if (paymentDetails.changeGiven !== undefined) {
    patch.changeGiven = Math.round(Number(paymentDetails.changeGiven) * 100) / 100;
  }
  if (paymentDetails.serviceCharge !== undefined) {
    patch.serviceCharge = Math.round(Number(paymentDetails.serviceCharge) * 100) / 100;
  }
  if (paymentDetails.finalTotal !== undefined) {
    patch.finalTotal = Math.round(Number(paymentDetails.finalTotal) * 100) / 100;
  }
  await db.commands.update(commandId, patch);
  const updated = await db.commands.get(commandId);

  // Fase 31 — registra venda automaticamente na sessão de caixa aberta (se houver)
  try {
    const saleAmount = Number(patch.finalTotal ?? updated?.total ?? 0);
    if (saleAmount > 0) {
      await recordSale({
        method: paymentMethod,
        amount: saleAmount,
        commandId,
        operatorId: operator?.id || null,
        operatorName: operator?.name || null,
      });
    }
  } catch (err) {
    // Silencioso: caixa fechado não impede fechar comanda
    // eslint-disable-next-line no-console
    console.warn('[cashDrawer] recordSale falhou:', err?.message || err);
  }

  return updated;
}

// Fase 8.5 - Abre comanda com item fixo "Café da Manhã" (sem pesagem)
export async function openCoffeeCommand(number, operatorId) {
  const price = (await db.config.get('coffeeComboPrice'))?.value ?? 15;
  const { command } = await getOrCreateCommand(number, operatorId);
  const items = [...(command.items || []), {
    name: '☕ Café da Manhã (Combo)',
    price: Number(price),
    quantity: 1,
    subtotal: Number(price),
    at: new Date().toISOString(),
    isCoffee: true,
  }];
  const itemsTotal = items.reduce((a, i) => a + i.subtotal, 0);
  const total = (command.buffetTotal || 0) + itemsTotal;
  await db.commands.update(command.id, { items, itemsTotal, total });
  return db.commands.get(command.id);
}

export async function getOpenCommands() {
  const all = await db.commands.where('status').equals('open').toArray();
  // Fase 25: comandas arquivadas não aparecem mais (dia encerrado)
  return all.filter((c) => !c.archivedAt).sort((a, b) => a.number - b.number);
}

export async function getCommandByNumber(number) {
  // Preferência para comanda ativa (não arquivada)
  const all = await db.commands.where('number').equals(Number(number)).toArray();
  return all.find((c) => !c.archivedAt) || null;
}

/**
 * Fase 23 / 25 — Retorna o próximo número de comanda disponível (1..MAX).
 * Considera "usado" qualquer comanda NÃO arquivada (open OU paid OU closed).
 * Quando o dia é encerrado (archivePaidCommands), os números voltam a ficar
 * livres — por isso a reinicialização funciona.
 */
export async function getNextAvailableCommandNumber(max = 99) {
  const all = await db.commands.toArray();
  const used = new Set(
    all.filter((c) => !c.archivedAt).map((c) => Number(c.number))
  );
  for (let n = 1; n <= max; n++) {
    if (!used.has(n)) return n;
  }
  return max + 1;
}

/**
 * Fase 25 — Arquiva todas as comandas pagas (status='paid') ainda não
 * arquivadas. Isso "zera" a numeração sem perder histórico: os registros
 * continuam no Dexie (relatórios, auditoria), mas `getNextAvailableCommandNumber`
 * e `getOrCreateCommand` passam a ignorá-los, liberando os números.
 *
 * Retorna { archived: N, lastArchivedAt }.
 */
export async function archivePaidCommands() {
  const all = await db.commands.where('status').equals('paid').toArray();
  const toArchive = all.filter((c) => !c.archivedAt);
  const now = new Date().toISOString();
  for (const c of toArchive) {
    await db.commands.update(c.id, { archivedAt: now });
  }
  if (toArchive.length > 0) {
    await logAudit({
      table: 'commands',
      action: 'update',
      record: { id: -1, number: 0 },
      modifications: null,
      overrideSummary: `Dia encerrado: ${toArchive.length} comanda${toArchive.length === 1 ? '' : 's'} arquivada${toArchive.length === 1 ? '' : 's'} e numeração zerada`,
    });
  }
  return { archived: toArchive.length, lastArchivedAt: now };
}

/**
 * Fase 23 — Remove um item da comanda pelo índice, recalcula totais,
 * devolve a baixa ao estoque (se era bebida) e registra no audit log
 * com detalhes específicos (item, quantidade, valor, operador).
 * Só funciona em comanda aberta. Retorna a comanda atualizada.
 */
export async function removeItemFromCommand(commandId, itemIndex, operatorId) {
  const command = await db.commands.get(commandId);
  if (!command) throw new Error('Comanda não encontrada');
  if (command.status !== 'open') throw new Error('Comanda já foi fechada');
  const items = Array.isArray(command.items) ? [...command.items] : [];
  const idx = Number(itemIndex);
  if (!Number.isInteger(idx) || idx < 0 || idx >= items.length) {
    throw new Error('Item inválido');
  }
  const removed = items[idx];
  items.splice(idx, 1);
  const itemsTotal = items.reduce((a, i) => a + Number(i.subtotal || 0), 0);
  const total = Number(command.buffetTotal || 0) + itemsTotal;
  await db.commands.update(commandId, { items, itemsTotal, total });

  // Devolve estoque silenciosamente (só para bebidas cadastradas)
  incrementBeverageStock({
    beverageId: removed?.beverageId,
    name: removed?.name,
    qty: removed?.quantity || 1,
  }).catch(() => {});

  // Auditoria explícita: registra o item removido com detalhes
  // (o hook genérico do Dexie já vai logar um "update", mas queremos
  // um evento específico para essa ação sensível).
  let operatorName = null;
  if (operatorId) {
    try {
      const op = await db.operators.filter((o) => o.opId === operatorId || o.id === operatorId).first();
      operatorName = op?.name || null;
    } catch {}
  }
  const priceBR = `R$ ${Number(removed?.subtotal || 0).toFixed(2).replace('.', ',')}`;
  await logAudit({
    table: 'commandItems',
    action: 'delete',
    record: { id: commandId, number: command.number },
    modifications: null,
    overrideSummary: `Comanda #${command.number}: ${operatorName || 'Operador'} removeu ${removed?.quantity || 1}× ${removed?.name || 'item'} (${priceBR})`,
  });

  return db.commands.get(commandId);
}


/**
 * Fase 30 — Tombstones de comandas excluídas pelo admin.
 * Implementação delegada para `lib/tombstones.js` (genérica para todas
 * as tabelas sincronizadas pelo Firebase). Mantemos os helpers daqui
 * por retrocompatibilidade.
 */

export async function getDeletedTombstones() {
  return getTombstones('commands');
}

export async function isCommandTombstoned(id) {
  const list = await getTombstones('commands');
  return list.some((t) => Number(t.id) === Number(id));
}

/**
 * Fase 30 — Admin: exclui UMA comanda permanentemente.
 * Hard-delete local + tombstone + remoção remota (best-effort).
 * Devolve bebidas ao estoque. Audit log com snapshot completo.
 */
export async function deleteCommand(commandId, operatorId) {
  const command = await db.commands.get(commandId);
  if (!command) throw new Error('Comanda não encontrada');

  if (command.status === 'open' && Array.isArray(command.items)) {
    for (const item of command.items) {
      if (item?.beverageId) {
        incrementBeverageStock({
          beverageId: item.beverageId,
          name: item.name,
          qty: item.quantity || 1,
        }).catch(() => {});
      }
    }
  }

  let operatorName = null;
  if (operatorId) {
    try {
      const op = await db.operators.filter((o) => o.opId === operatorId || o.id === operatorId).first();
      operatorName = op?.name || null;
    } catch {}
  }

  const totalBR = `R$ ${Number(command.total || 0).toFixed(2).replace('.', ',')}`;
  const itemCount = (command.items || []).length;
  const weightCount = (command.weightEntries || []).length;

  await logAudit({
    table: 'commands',
    action: 'delete',
    record: { id: commandId, number: command.number },
    modifications: null,
    overrideSummary: `${operatorName || 'Admin'} excluiu comanda #${command.number} (${command.status}) — ${totalBR}, ${itemCount} item(ns), ${weightCount} pesagem(ns)`,
  });

  // 1) Tombstone primeiro — garante que mesmo se o delete falhar, o sync ignora
  await addTombstone('commands', commandId, { number: command.number, by: operatorName });
  // 2) Hard-delete local — dispara hook 'deleting' que chama removeFromRemote
  await db.commands.delete(commandId);
  return { ok: true, number: command.number };
}

/**
 * Fase 30 — Admin: exclui TODAS as comandas abertas.
 * Hard-delete + tombstones em massa + remoção remota.
 */
export async function deleteAllOpenCommands(operatorId) {
  const open = await db.commands.where('status').equals('open').toArray();
  const active = open.filter((c) => !c.archivedAt);
  if (active.length === 0) return { deleted: 0, totalValue: 0 };

  let operatorName = null;
  if (operatorId) {
    try {
      const op = await db.operators.filter((o) => o.opId === operatorId || o.id === operatorId).first();
      operatorName = op?.name || null;
    } catch {}
  }

  // Devolve estoque
  for (const c of active) {
    for (const item of (c.items || [])) {
      if (item?.beverageId) {
        incrementBeverageStock({
          beverageId: item.beverageId,
          name: item.name,
          qty: item.quantity || 1,
        }).catch(() => {});
      }
    }
  }

  const totalValue = active.reduce((a, c) => a + Number(c.total || 0), 0);
  const totalBR = `R$ ${totalValue.toFixed(2).replace('.', ',')}`;

  await logAudit({
    table: 'commands',
    action: 'delete',
    record: { id: -1, number: 0 },
    modifications: null,
    overrideSummary: `${operatorName || 'Admin'} excluiu em massa ${active.length} comanda(s) aberta(s) — total cancelado: ${totalBR}`,
  });

  // Tombstones em massa primeiro (idempotente)
  for (const c of active) {
    await addTombstone('commands', c.id, { number: c.number, by: operatorName });
  }
  // Hard-delete em massa — cada delete dispara hook que tenta removeFromRemote
  await db.commands.bulkDelete(active.map((c) => c.id));
  return { deleted: active.length, totalValue };
}
